import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  readNote,
  deleteTransfer,
  downloadTransfer,
  formatBytes,
  listTransfers,
  MAX_FILE_SIZE,
  uploadFile,
} from "./api.js";

import PhoneLink from "./PhoneLink.jsx";
import { createPairKey, extractPairKey, readPairing, pairingUrl, transferDirections, PAIR_STORAGE, ROLE_STORAGE, DESKTOP_DOWNLOAD } from "./pairing.js";
const DISMISSED_KEY = "emx_notecore_dismissed";

function dismissedTransfers() {
  try {
    const values = JSON.parse(localStorage.getItem(DISMISSED_KEY) || "{}");
    const cutoff = Date.now() - 14 * 24 * 60 * 60 * 1000;
    return Object.fromEntries(Object.entries(values).filter(([, timestamp]) => Number(timestamp) > cutoff));
  } catch { return {}; }
}

function rememberDismissed(id) {
  const values = dismissedTransfers();
  values[id] = Date.now();
  try { localStorage.setItem(DISMISSED_KEY, JSON.stringify(values)); } catch { /* Relay deletion still succeeds without local storage. */ }
}

function useToast() {
  const [toast, setToast] = useState(null);
  const timer = useRef(null);
  const show = useCallback((message, kind = "info") => {
    clearTimeout(timer.current);
    setToast({ message, kind });
    timer.current = setTimeout(() => setToast(null), 4200);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return { toast, show };
}

function TransferCard({ item, inbox, onRemoved, show }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [previewAttempt, setPreviewAttempt] = useState(0);
  const isNote = item.name.endsWith(".emxnote.json");
  useEffect(() => {
    if (!isNote) return;
    const controller = new AbortController();
    setPreviewError("");
    readNote(inbox, item, controller.signal).then(setPreview).catch(error => {
      if (!controller.signal.aborted) setPreviewError(error.message);
    });
    return () => controller.abort();
  }, [inbox, item.id, isNote, previewAttempt]);
  const date = new Date((item.completedAt || item.createdAt) * 1000);

  async function download() {
    setBusy(true);
    try {
      await downloadTransfer(inbox, item);
      show(`${item.name} downloaded.`, "success");
    } catch (error) {
      show(error.message, "error");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await deleteTransfer(inbox, item.id);
      onRemoved(item.id);
      show("Removed from the private relay.", "success");
    } catch (error) {
      show(error.message, "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="transferCard">
      <div className="fileIcon">{item.contentType?.startsWith("image/") ? "IMG" : "FILE"}</div>
      <div className="fileMeta">
        <strong>{item.name}</strong>
        <span>{formatBytes(item.size)} · {date.toLocaleString()}</span>
      </div>
      {isNote && <div className="notePreview">{preview ? <><h4>{preview.title}</h4><p>{preview.text}</p><button className="smallButton" onClick={async () => {
        try { await navigator.clipboard.writeText(preview.text); show("Note copied.", "success"); }
        catch { show("Select the note text to copy it manually.", "error"); }
      }}>Copy note</button></> : previewError ? <><p role="alert">{previewError}</p><button className="smallButton" onClick={() => setPreviewAttempt(value => value + 1)}>Retry preview</button></> : <p role="status">Loading note…</p>}</div>}
      <div className="cardActions">
        <button className="smallButton" disabled={busy} onClick={download}>Download</button>
        <button className="iconButton danger" aria-label={`Delete ${item.name}`} disabled={busy} onClick={remove}>×</button>
      </div>
    </article>
  );
}

export default function App() {
  const initialPair = useMemo(() => {
    let storage;
    try { storage = window.localStorage; } catch {}
    return readPairing(window.location.href, storage);
  }, []);
  const [inbox, setInbox] = useState(initialPair.key);
  const [role, setRole] = useState(initialPair.role);
  const directions = transferDirections(role);
  const recipient = role === "pc" ? "phone" : "PC";
  const sender = role === "pc" ? "phone" : "PC";
  const [inboxError, setInboxError] = useState("");
  const uploadLock = useRef(false);
  const activeInbox = useRef(inbox);
  activeInbox.current = inbox;
  const [pairInput, setPairInput] = useState("");
  const [tab, setTab] = useState("note");
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [priority, setPriority] = useState("NORMAL");
  const [tags, setTags] = useState("");
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeName, setActiveName] = useState("");
  const [online, setOnline] = useState(navigator.onLine);
  const [installPrompt, setInstallPrompt] = useState(null);
  const fileInput = useRef(null);
  const { toast, show } = useToast();

  const refreshInbox = useCallback(async (quiet = false) => {
    if (!inbox) return;
    if (!quiet) setLoadingFiles(true);
    try {
      const result = await listTransfers(inbox, directions.receive);
      if (activeInbox.current !== inbox) return;
      const dismissed = dismissedTransfers();
      setFiles((result.files || []).filter((item) => !dismissed[item.id]));
      setInboxError("");
    } catch (error) {
      if (activeInbox.current !== inbox) return;
      setInboxError(error.message);
      if (!quiet) show(error.message, "error");
    } finally {
      if (!quiet) setLoadingFiles(false);
    }
  }, [inbox, directions.receive, show]);

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    const onInstall = (event) => { event.preventDefault(); setInstallPrompt(event); };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("beforeinstallprompt", onInstall);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("beforeinstallprompt", onInstall);
    };
  }, []);

  useEffect(() => {
    if (!inbox) return;
    try {
      localStorage.setItem(PAIR_STORAGE, inbox);
      localStorage.setItem(ROLE_STORAGE, role);
    } catch { show("Browser storage is unavailable. Keep this tab open or save its private link to reconnect.", "error"); }
    window.history.replaceState(null, "", pairingUrl(window.location.origin, inbox, role));
    refreshInbox(true);
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") refreshInbox(true);
    }, 10_000);
    return () => clearInterval(timer);
  }, [inbox, role, refreshInbox, show]);

  function pair() {
    const key = extractPairKey(pairInput);
    if (!key) return show("Paste the QR link or its 64-character pairing key.", "error");
    setRole("phone");
    setInbox(key);
    setPairInput("");
    show("Phone paired with EMX NoteCore.", "success");
  }

  async function sendBlob(file) {
    setUploading(true);
    setActiveName(file.name);
    setProgress(0);
    try {
      await uploadFile({ inbox, direction: directions.send, file, onProgress: setProgress });
      show(`${file.name} sent to your ${recipient}. It may take a moment to appear.`, "success");
      return true;
    } catch (error) {
      show(error.message, "error");
      return false;
    } finally {
      setUploading(false);
      setActiveName("");
      setProgress(0);
    }
  }

  async function sendNote() {
    if (!note.trim()) return show("Write something before sending the note.", "error");
    if (uploadLock.current) return;
    const safeTitle = (title.trim() || (role === "pc" ? "PC Note" : "Phone Note")).slice(0, 80);
    const payload = {
      type: "emx-note",
      title: safeTitle,
      text: note,
      tags: tags.split(",").map((tag) => tag.trim()).filter(Boolean).slice(0, 12),
      priority,
      createdAt: new Date().toISOString(),
    };
    const file = new File([JSON.stringify(payload, null, 2)], `${safeTitle.replace(/[^a-z0-9 _-]/gi, "_")}.emxnote.json`, { type: "application/json" });
    uploadLock.current = true;
    const sent = await sendBlob(file);
    uploadLock.current = false;
    if (sent) {
      setTitle("");
      setNote("");
      setTags("");
      setPriority("NORMAL");
    }
  }

  async function sendFiles(selected) {
    if (uploadLock.current) return;
    uploadLock.current = true;
    try {
      for (const file of selected) {
        if (file.size > MAX_FILE_SIZE) {
          show(`${file.name} is larger than 384 MB.`, "error");
          continue;
        }
        await sendBlob(file);
      }
    } finally { uploadLock.current = false; if (fileInput.current) fileInput.current.value = ""; }
  }

  async function refreshApp() {
    show("Checking for the newest EMX NoteCore…");
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.update()));
    }
    window.location.reload();
  }

  async function install() {
    if (!installPrompt) return show("Use your browser Share menu, then Add to Home Screen.");
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  }

  if (!inbox) {
    return (
      <main className="pairPage">
        <section className="pairCard glass">
          <img src="/emx-logo.png" className="pairLogo" alt="EMX" />
          <p className="eyebrow">EMX NOTES · PHONE ⇄ PC</p>
          <h1>Your notes.<br />Both screens.</h1>
          <p className="lead">Get EMX NoteCore for Windows, or connect your PC browser and phone without downloading anything. Send notes and files both ways.</p>
          <a className="primaryButton downloadButton" href={DESKTOP_DOWNLOAD}>Download EMX Notes for Windows <span aria-hidden="true">↓</span></a>
          <p className="downloadMeta">NoteCore 6.0 · Windows x64 · 69.5 MB</p>
          <button className="quietButton browserStart" onClick={() => {
            try { setRole("pc"); setInbox(createPairKey()); }
            catch { show("A secure browser connection is needed to create a private QR. Open this page using HTTPS and try again.", "error"); }
          }}>Use EMX Notes in this browser <span aria-hidden="true">↗</span></button>
          <p className="lead">Using the browser? Open the workspace above, then scan its QR with your phone camera. Using the Windows app? Open <b>Phone Link</b> in the app and scan its QR.</p>
          <details className="manualPair"><summary>Already have a pairing link?</summary>
          <label htmlFor="pairKey">Pairing link or key</label>
          <textarea id="pairKey" value={pairInput} onChange={(event) => setPairInput(event.target.value)} placeholder="https://notes.emxtweaks.com/#inbox=…" />
          <button className="primaryButton" onClick={pair}>Pair this phone</button>
          </details>
          <div className="privacyRow"><span>256-bit private key</span><span>No public accounts</span><span>7-day relay cleanup</span></div>
        </section>
        {toast && <div role="status" className={`toast ${toast.kind}`}>{toast.message}</div>}
      </main>
    );
  }

  return (
    <main className="appShell">
      <header className="topbar glass">
        <div className="brand">
          <img src="/emx-logo.png" alt="EMX" />
          <div><p>EMX</p><h1>Notes</h1></div>
        </div>
        <div className={`status ${online ? "online" : "offline"}`}><i />{online ? "ONLINE" : "OFFLINE"}</div>
        <div className="headerActions">
          {installPrompt && <button className="quietButton" onClick={install}>Install</button>}
          <button className="iconButton" aria-label="Refresh app" onClick={refreshApp}>↻</button>
        </div>
      </header>

      {role === "pc" && <PhoneLink inbox={inbox} show={show} />}

      <section className="hero">
        <p className="eyebrow">PHONE ⇄ PC WORKSPACE</p>
        <h2>Move the thought.<br /><span>Keep the momentum.</span></h2>
        <p>Write here. Read on your other screen. Keep both devices connected to receive new notes and files.</p>
      </section>

      <nav className="tabs" aria-label="NoteCore sections">
        <button className={tab === "note" ? "active" : ""} onClick={() => setTab("note")}>New note</button>
        <button className={tab === "file" ? "active" : ""} onClick={() => setTab("file")}>Send files</button>
        <button className={tab === "fromPc" ? "active" : ""} onClick={() => { setTab("fromPc"); refreshInbox(); }}>From {sender} <b>{files.length}</b></button>
      </nav>

      <section className="workspace glass">
        {tab === "note" && (
          <div className="composerPanel">
            <div className="sectionTitle"><div><p className="eyebrow">QUICK CAPTURE</p><h3>Send a note to your {recipient}</h3></div><span className="keyBadge">PAIR …{inbox.slice(-6)}</span></div>
            <label htmlFor="title">Title</label>
            <input id="title" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="What is this about?" maxLength={80} />
            <label htmlFor="note">Note</label>
            <textarea id="note" className="noteInput" value={note} onChange={(event) => setNote(event.target.value)} placeholder="Type or paste anything…" />
            <div className="twoColumns">
              <div><label htmlFor="tags">Tags</label><input id="tags" value={tags} onChange={(event) => setTags(event.target.value)} placeholder="setup, idea, important" /></div>
              <div><label htmlFor="priority">Priority</label><select id="priority" value={priority} onChange={(event) => setPriority(event.target.value)}><option>NORMAL</option><option>HIGH</option><option>URGENT</option></select></div>
            </div>
            <button className="primaryButton" disabled={uploading} onClick={sendNote}>{uploading ? `Sending ${progress}%` : `Send note to ${recipient}`}</button>
          </div>
        )}

        {tab === "file" && (
          <div className="filePanel">
            <div className="sectionTitle"><div><p className="eyebrow">RETRYABLE TRANSFER</p><h3>Send files to your {recipient}</h3></div><span className="limitBadge">UP TO 384 MB</span></div>
            <button
              className={`dropZone ${uploading ? "busy" : ""}`}
              disabled={uploading}
              onClick={() => fileInput.current?.click()}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => { event.preventDefault(); sendFiles([...event.dataTransfer.files]); }}
            >
              <span className="dropIcon">＋</span>
              <strong>{uploading ? activeName : "Choose files"}</strong>
              <small>{uploading ? `${progress}% uploaded` : "Tap to browse or drop them here"}</small>
              {uploading && <i className="progress"><i style={{ width: `${progress}%` }} /></i>}
            </button>
            <input ref={fileInput} hidden type="file" multiple onChange={(event) => sendFiles([...event.target.files])} />
            <div className="featureGrid"><div><b>8 MB</b><span>retryable chunks</span></div><div><b>7 DAYS</b><span>automatic cleanup</span></div><div><b>PRIVATE</b><span>pair-key protected</span></div></div>
          </div>
        )}

        {tab === "fromPc" && (
          <div className="inboxPanel">
            <div className="sectionTitle"><div><p className="eyebrow">FROM YOUR {sender.toUpperCase()}</p><h3>{role === "pc" ? "Browser inbox" : "Phone inbox"}</h3></div><button className="quietButton" disabled={loadingFiles} onClick={() => refreshInbox()}>{loadingFiles ? "Checking…" : "Refresh"}</button></div>
            {inboxError && <p className="inlineError" role="alert">{inboxError} Use Refresh to retry.</p>}
            {files.length === 0 ? (
              <div className="emptyState"><span>⇄</span><h4>Nothing waiting</h4><p>Notes and files sent from your {sender} will appear here. Delivery may take a moment; keep both screens open.</p></div>
            ) : files.map((item) => <TransferCard key={item.id} item={item} inbox={inbox} show={show} onRemoved={(id) => { rememberDismissed(id); setFiles((current) => current.filter((entry) => entry.id !== id)); }} />)}
          </div>
        )}
      </section>

      <footer>
        <button onClick={() => {
          if (window.confirm("Disconnect this browser? Waiting items remain in the relay until deleted or expired. Save the private link first if you want to reconnect.")) {
            try { localStorage.removeItem(PAIR_STORAGE); localStorage.removeItem(ROLE_STORAGE); } catch {}
            setFiles([]); setInboxError(""); setTab("note");
            const url = new URL(window.location.href); url.searchParams.delete("inbox"); url.hash = ""; window.history.replaceState(null, "", url); setInbox("");
          }
        }} disabled={uploading}>Disconnect this browser</button>
        <span>Encrypted in transit · Waiting items expire after 7 days</span>
      </footer>
      {toast && <div role="status" className={`toast ${toast.kind}`}>{toast.message}</div>}
    </main>
  );
}
